import React, { useState } from "react";
import { ClientApiProps, AlphaTagPicker } from "../../common";
import { ErrorScope, Label, Modal, Spinner, TextInput, useValidatedState } from "acma-spa";
import { AuthorisationRequest, IAlphaTag, ISettings, Settings, SettingsBase, getApiClient } from "../../api";
import { notEmpty } from "../../stores/ListCache";
import { RegisterStore } from "../../stores/RegisterStore";
import * as yup from "yup";
import { SettingsStore } from "../../stores/SettingsStore";

export interface RequestModalProps extends ClientApiProps {
  open: boolean
  onClose: () => any
  onChange: () => Promise<any>
  selection: IAlphaTag[]
  settings: ISettings[]
  onSettings: (v: ISettings[]) => any
}

interface Model {
  contactName: string,
  contactEmail: string,
  alphaTags: IAlphaTag[],
  authorisationMailbox: string
}

const schema: yup.ObjectSchema<Model> = yup.object({
  contactName: yup.string().required().label('Contact name'),
  contactEmail: yup.string().email().required().label('Contact email'),
  alphaTags: yup.array().defined().min(1).max(10).label('Alpha tags'),
  authorisationMailbox: yup.string().defined().email(),
});


export const RequestModal: React.FC<RequestModalProps> = (props) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [spinner, setSpinner] = useState<string>("");
  const [defaultOptions, setDefaultOptions] = useState<IAlphaTag[]>(() => props.selection.slice());

  const [model, setModel, errors, validate] = useValidatedState<Model>(() => ({
    contactName: '',
    contactEmail: '',
    alphaTags: props.selection.slice(),
    authorisationMailbox: props.settings.filter(i => i.isParticipant)[0]?.authorisationMailbox ?? ''
  }), schema);

  const onRequest = async () => {
    var res = await validate('#requestContainer');
    if (!res) {
      return;
    }

    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );
    const store = new RegisterStore(client);
    const settingsStore = new SettingsStore(client)

    setSpinner("Requesting...");
    setError("");

    try {
      if (model.authorisationMailbox != (props.settings.filter(i => i.isParticipant)[0]?.authorisationMailbox ?? '')) {
        var temp = await settingsStore.updateSettings([
          new SettingsBase({ ...props.settings.filter(i => i.isParticipant)[0], authorisationMailbox: model.authorisationMailbox })
        ]);
        if (temp.length > 0) {
          var newSettings = props.settings.slice();
          newSettings.splice(0, 1, temp[0]);
          props.onSettings(newSettings);
        }
      }

      await store.requestAuthorisations(new AuthorisationRequest({
        alphaTagIds: model.alphaTags.map(r => r.alphaTagId).filter(notEmpty),
        contactName: model.contactName,
        contactEmail: model.contactEmail
      }));
      await props.onChange();
      props.onClose();
    }
    catch (e: any) {
      setError(e.message ? e.message : "Unexpected error");
    }
    finally {
      setSpinner("");
    }
  }

  return <>
    {props.open ? <Modal
      id="requestModal"
      size="lg"
      open={true}
      onClose={() => !spinner && props.onClose()}
      title="Confirmation request"
      onOpened={() => document.getElementById('alphaTags')?.focus()}
      keyboard={false}
      backdrop= "static"
      leftButtons={[
        {
          label: "Request",
          id: "request",
          onClick: () => onRequest(),
          disabled: spinner ? true : false
        },
        {
          label: "Cancel",
          id: "cancel",
          onClick: () => props.onClose(),
          mode: 'secondary'
        }
      ]}>
      <div id="requestContainer" className="mb-2">
        <Spinner isLoading={spinner ? true : false} loadingText={spinner}>
          <Label id="alphaTags" label="Alpha tag(s)" hint="Alpha tags you are including in the request" error={errors.alphaTags as string} required>
            <AlphaTagPicker {...props} value={model.alphaTags} onChange={v => setModel({ alphaTags: v })} defaultOptions={defaultOptions} register={true} />
          </Label>
          <Label id="contactName" label="Contact name" hint="Name of the person who claims to be company representative." error={errors.contactName} required className="mt-3">
            <TextInput value={model.contactName} onChange={v => setModel({ contactName: v })} maxLength={400} />
          </Label>
          <Label id="contactEmail" label="Contact email" hint="Email address of the person who claims to be company representative." error={errors.contactEmail} required className="mt-3">
            <TextInput value={model.contactEmail} onChange={v => setModel({ contactEmail: v })} maxLength={400} />
          </Label>
          {!props.settings.filter(i => i.isParticipant)[0].authorisationMailbox ? <Label label="Mailbox to receive confirmation notifications" id="company" hint="Mailbox where to receive emails when requested confirmation has been approved or revoked." className="mt-3" error={errors.authorisationMailbox}>
            <TextInput value={model.authorisationMailbox ?? ''} onChange={v => setModel({ authorisationMailbox: v })} maxLength={400} />
          </Label> : ''}
        </Spinner>
      </div>
      <ErrorScope id="error" error={error} />
    </Modal> : <></>}
  </>
}